import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HelpCircle, Check, Eraser } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { startTour } from "@/components/Tour";
import { CommandPalette } from "@/components/CommandPalette";
import {
  Compass, Inbox as InboxIcon, Search, Telescope, FileSpreadsheet,
  LayoutDashboard, Package, BookOpen, UploadCloud, Terminal, Building2, Users, Truck, UserSquare2, ShoppingCart,
  Boxes, Receipt, LogOut, MapPin, Handshake, Bell, ShieldCheck, Globe2, SlidersHorizontal, Satellite, Route, GitMerge, PackageSearch, FileCheck2, BarChart3, Send, History, Warehouse, CalendarCheck,
} from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";

function Bell2() {
  const [d, setD] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const f = () => api.get("/notifications").then((r) => setD(r.data)).catch(() => {});
    f();
    const t = setInterval(f, 30000);
    return () => clearInterval(t);
  }, []);
  const toggle = async () => {
    setOpen((o) => !o);
    if (!open && d.unread) { await api.post("/notifications/read"); setD((x) => ({ ...x, unread: 0 })); }
  };
  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={toggle} className="relative rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
        <Bell className="h-4 w-4" />
        {d.unread > 0 && <span data-testid="notifications-unread" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D52B3B] px-1 text-[10px] font-bold text-white">{d.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-xl" data-testid="notifications-panel">
          {!d.items.length && <p className="p-3 text-sm text-slate-500">No notifications.</p>}
          {d.items.map((n) => (
            <button key={n.id} onClick={() => { setOpen(false); nav(n.link); }} className="block w-full rounded-lg p-2.5 text-left hover:bg-slate-50">
              <p className="text-sm font-medium text-slate-800">{n.title}</p>
              <p className="text-xs text-slate-500">{n.body} · {fmtDate(n.created_at, true)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NAV = [
  {
    group: "Overview",
    items: [
      { to: "/", label: "Readiness", icon: Compass, id: "readiness", home: true },
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", am: true },
      { to: "/inbox", label: "Inbox", icon: InboxIcon, id: "inbox", am: true, badge: true },
    ],
  },
  {
    group: "Compliance",
    items: [
      { to: "/materials", label: "Materials", icon: Package, id: "materials" },
      { to: "/annex", label: "Annex I codes", icon: BookOpen, id: "annex" },
      { to: "/annex/lookup", label: "Code lookup", icon: Search, id: "code-lookup" },
      { to: "/suppliers", label: "Suppliers", icon: Handshake, id: "suppliers" },
      { to: "/risk", label: "Risk dashboard", icon: ShieldCheck, id: "risk" },
      { to: "/flows", label: "EUDR flows", icon: Route, id: "flows" },
      { to: "/explorer", label: "Scope explorer", icon: Telescope, id: "explorer", am: true },
      { to: "/mapping", label: "Product mapping", icon: GitMerge, id: "mapping" },
      { to: "/untraceable", label: "Untraceable products", icon: PackageSearch, id: "untraceable" },
      { to: "/dds", label: "DDS register", icon: FileCheck2, id: "dds" },
      { to: "/dds/dashboard", label: "DDS dashboard", icon: BarChart3, id: "dds-dashboard" },
      { to: "/overview", label: "Last month overview", icon: CalendarCheck, id: "overview", am: true },
      { to: "/packs", label: "Customer packs", icon: Package, id: "packs", am: true },
      { to: "/customs", label: "Customs DDS export", icon: FileSpreadsheet, id: "customs", roles: ["admin", "compliance", "purchasing"] },
    ],
  },
  {
    group: "SAP data",
    items: [
      { to: "/uploads", label: "Uploads", icon: UploadCloud, id: "uploads" },
      { to: "/scripts", label: "Scripts & API keys", icon: Terminal, id: "scripts" },
      { to: "/data/vendors", label: "Vendors", icon: Truck, id: "vendors" },
      { to: "/data/customers", label: "Customers", icon: UserSquare2, id: "customers", am: true },
      { to: "/data/po_lines", label: "PO lines", icon: ShoppingCart, id: "po-lines" },
      { to: "/data/stock", label: "Stock", icon: Boxes, id: "stock" },
      { to: "/data/sales", label: "Sales", icon: Receipt, id: "sales", am: true },
    ],
  },
  {
    group: "Settings",
    items: [
      { to: "/plants", label: "Plants & entities", icon: Building2, id: "plants" },
      { to: "/settings/countries", label: "Country risk", icon: Globe2, id: "country-risk" },
      { to: "/settings/rules", label: "Risk rules", icon: SlidersHorizontal, id: "risk-rules" },
      { to: "/settings/gfw", label: "Global Forest Watch", icon: Satellite, id: "gfw" },
      { to: "/settings/traces", label: "TRACES connection", icon: Send, id: "traces" },
      { to: "/settings/notifications", label: "Notifications", icon: Bell, id: "notifications" },
      { to: "/vendor-plants", label: "Vendor default plants", icon: Warehouse, id: "vendor-plants" },
      { to: "/audit", label: "Audit log", icon: History, id: "audit" },
      { to: "/account-managers", label: "Account managers", icon: UserSquare2, id: "account-managers", admin: true },
      { to: "/settings/housekeeping", label: "Data housekeeping", icon: Eraser, id: "housekeeping", admin: true },
      { to: "/users", label: "Users", icon: Users, id: "users", admin: true },
    ],
  },
];

function InboxBadge() {
  const [n, setN] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    const f = () => api.get("/inbox/count").then((r) => setN(r.data.count)).catch(() => {});
    f();
    window.addEventListener("inbox-changed", f);
    const t = setInterval(f, 60000);
    return () => { clearInterval(t); window.removeEventListener("inbox-changed", f); };
  }, [loc.pathname]);
  if (!n) return null;
  return <span data-testid="inbox-badge" className="ml-auto rounded-full bg-[#D52B3B] px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white">{n > 99 ? "99+" : n}</span>;
}

function Sidebar() {
  const { can, user } = useAuth();
  return (
    <aside className="grain fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-[#1C1C1E] text-slate-300" data-testid="sidebar">
      <div className="px-5 pb-5 pt-6">
        <img src="/logo-epg-mark.png" alt="EP Group" className="h-9 w-auto" data-testid="sidebar-logo" />
        <p className="mt-3 font-display text-[15px] font-semibold tracking-tight text-white">EUDR Compass</p>
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">by EP Group</p>
      </div>
      <nav className="scroll-thin flex-1 space-y-6 overflow-y-auto px-3 pb-6">
        {NAV.map((g) => ({ ...g, items: g.items.filter((i) => (can.accountManager ? i.am : (!i.admin || can.admin) && (!i.roles || i.roles.includes(user?.role)) && !(i.home && user?.role === "viewer"))) })).filter((g) => g.items.length).map((g) => (
          <div key={g.group}>
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{g.group}</p>
            {g.items
              .map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/" || i.to === "/dds" || i.to === "/annex"}
                  data-testid={`nav-${i.id}`}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors duration-150",
                      isActive ? "bg-white/[0.07] text-white" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={cn("absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-[#D52B3B] transition-opacity", isActive ? "opacity-100" : "opacity-0")} />
                      <i.icon className={cn("h-4 w-4", isActive ? "text-[#D52B3B]" : "")} />
                      {i.label}
                      {i.badge && <InboxBadge />}
                    </>
                  )}
                </NavLink>
              ))}
          </div>
        ))}
      </nav>
      <p className="px-5 pb-4 text-[10px] text-slate-600">Phase 8 · Faster portal & tours</p>
    </aside>
  );
}

export function PlantSelector() {
  const { plants, entities, selection, setSelection } = usePlant();
  const { user } = useAuth();
  return (
    <Select value={selection} onValueChange={setSelection}>
      <SelectTrigger className="h-9 w-[230px] bg-white" data-testid="global-plant-selector">
        <MapPin className="mr-1 h-4 w-4 text-[#D52B3B]" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="plant-option-all">
          {user?.role === "purchasing" ? "All my plants" : "All plants"}
        </SelectItem>
        {user?.role !== "purchasing" && entities.length > 0 && (
          <>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>Legal entity</SelectLabel>
              {entities.map((e) => (
                <SelectItem key={e.id} value={`entity:${e.id}`} data-testid={`plant-option-entity-${e.code}`}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </>
        )}
        <SelectSeparator />
        <SelectGroup>
          <SelectLabel>Plant</SelectLabel>
          {plants.map((p) => (
            <SelectItem key={p.code} value={`plant:${p.code}`} data-testid={`plant-option-${p.code}`}>
              {p.code} {p.name && p.name !== `Plant ${p.code}` ? `· ${p.name}` : ""} {p.country ? `(${p.country})` : ""}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function HelpMenu() {
  const { user } = useAuth();
  const navTo = useNavigate();
  const [lang, setLang] = useState(user?.language || "en");
  const started = useRef(false);
  useEffect(() => {
    if (!user || user.tour || started.current) return undefined;
    let n = 0;
    const t = setInterval(() => {
      if (started.current) return clearInterval(t);
      if (document.querySelector('[data-testid^="nav-"]') || ++n > 20) {
        started.current = true;
        clearInterval(t);
        startTour(user.role, user.language || "en");
      }
      return undefined;
    }, 500);
    return () => clearInterval(t);
  }, [user]);
  const pick = (l) => api.put("/auth/me/preferences", { language: l }).then(() => { setLang(l); toast.success(l === "fr" ? "Langue des visites : français" : "Tour language: English"); });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="help-menu-btn" className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800" title="Help"><HelpCircle className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem data-testid="help-process-guide-btn" onClick={() => navTo("/guide")}><BookOpen className="mr-2 h-4 w-4" />Process guide</DropdownMenuItem>
        <DropdownMenuItem data-testid="help-tour-btn" onClick={() => setTimeout(() => startTour(user.role, lang), 150)}><Compass className="mr-2 h-4 w-4" />{lang === "fr" ? "Refaire la visite" : "Take the tour again"}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <p className="px-2 py-1 text-[11px] uppercase tracking-wider text-slate-400">{lang === "fr" ? "Langue des visites" : "Tour language"}</p>
        {["en", "fr"].map((l) => <DropdownMenuItem key={l} data-testid={`help-lang-${l}`} onClick={() => pick(l)}>{l === "en" ? "English" : "Français"}{lang === l && <Check className="ml-auto h-4 w-4 text-[#D52B3B]" />}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Topbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-8 backdrop-blur-xl">
      <div className="flex items-center gap-3">
        {user?.role !== "account_manager" && <PlantSelector />}
        <button data-testid="global-search-btn" onClick={() => window.dispatchEvent(new Event("open-search"))} className="flex h-9 w-72 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600">
          <Search className="h-4 w-4" />Search everything…<kbd className="ml-auto rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] font-medium text-slate-500">{navigator.platform?.includes("Mac") ? "⌘" : "Ctrl"} K</kbd>
        </button>
      </div>
      <CommandPalette />
      <div className="flex items-center gap-4">
        <Bell2 />
        <HelpMenu />
        <div className="text-right">
          <p className="text-sm font-medium text-slate-800" data-testid="current-user-name">{user?.name}</p>
          <p className="text-[11px] text-slate-500" data-testid="current-user-role">
            {ROLE_LABELS[user?.role]}
            {user?.role === "purchasing" && user.plants?.length ? ` · ${user.plants.join(", ")}` : ""}
          </p>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => {
            logout();
            nav("/login");
          }}
          className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800"
          title="Log out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Sidebar />
      <div className="pl-64">
        <Topbar />
        <main className="mx-auto max-w-[1500px] px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
